import * as yup from "yup";
import { SubscriptionCustomerCompanyKey } from "../../models/Subscription";

export interface AddNewSubscriptionRequest {
	customerKey: string;
	customerKeyDescription: string;
	autoRenew: boolean;
	isNewCompany: boolean;
	companyId: string;
	company: AddNewSubscriptionRequestCompany;
	contacts: AddNewSubscriptionRequestContact[];
	isNewBillingPlan: boolean;
	billingPlanId: string;
	billingPlan: AddNewSubscriptionRequestBillingPlan;
	productTierXBillingPlanId: string;
	productTierXBillingPlan: AddNewSubscriptionRequestProductTierXBillingPlan;
	termsOfServiceAcceptance?: AddNewSubscriptionRequestTermsOfServiceAcceptance;
}

export interface AddNewSubscriptionRequestProductTierXBillingPlan {
	productTierId: string;
	billingPlanId: string;
	invoiceDescription: string;
}

export interface AddNewSubscriptionRequestBillingPlan {
	billingPlanName: string;
	costFixed: number | null;
	costResource: number | null;
	costUsage: number | null;
	currency: string;
	defaultAutoRenew: boolean;
	invoiceAtStartOfPeriod: boolean;
	isCustom: boolean;
	isEnabledForPurchase: boolean;
	licenseTypeId: string;
	period: string;
	resourceCountMaximum: number | null;
	resourceCountMinimum: number | null;
	paymentTermInDays: number | null;
	fixedFeeTypeLabelId: string;
	resourceFeeTypeLabelId: string;
	usageFeeTypeLabelId: string;
	usageMetricMaximum: number | null;
	usageMetricMinimum: number | null;
}

export interface AddNewSubscriptionRequestCompany {
	companyName: string;
	identityProviderId: string;
	uniqueCompanyIdentifier: string;
}

export interface AddNewSubscriptionRequestContact {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	phoneNumberCountryCode: string;
}

export interface AddNewSubscriptionRequestTermsOfServiceAcceptance {
	acceptor: string;
	dateAccepted: string;
	termsOfServiceId: string;
}

export const AddNewSubscriptionRequestContactInitialValue: AddNewSubscriptionRequestContact = {
	firstName: "",
	lastName: "",
	email: "",
	phoneNumber: "",
	phoneNumberCountryCode: "",
};

export const AddNewSubscriptionRequestInitialValue: AddNewSubscriptionRequest = {
	customerKey: "",
	customerKeyDescription: "",
	autoRenew: false,
	isNewCompany: false,
	companyId: "",
	company: {
		companyName: "",
		identityProviderId: "",
		uniqueCompanyIdentifier: "",
	},
	contacts: [AddNewSubscriptionRequestContactInitialValue],
	isNewBillingPlan: false,
	billingPlanId: "",
	billingPlan: {
		billingPlanName: "",
		costFixed: null,
		costResource: null,
		costUsage: null,
		currency: "",
		defaultAutoRenew: false,
		invoiceAtStartOfPeriod: false,
		isCustom: false,
		isEnabledForPurchase: false,
		licenseTypeId: "",
		period: "",
		resourceCountMaximum: null,
		resourceCountMinimum: null,
		paymentTermInDays: null,
		fixedFeeTypeLabelId: "",
		resourceFeeTypeLabelId: "",
		usageFeeTypeLabelId: "",
		usageMetricMaximum: null,
		usageMetricMinimum: null,
	},
	productTierXBillingPlanId: "",
	productTierXBillingPlan: {
		billingPlanId: "",
		productTierId: "",
		invoiceDescription: "",
	},
	termsOfServiceAcceptance: {
		acceptor: "",
		dateAccepted: "",
		termsOfServiceId: "",
	},
};

export const AddNewSubscriptionRequestValidationSchema = (
	existingBillingPlanNames: string[],
	existingCompanyNames: string[],
	existingCompanyUniqueNames: string[],
	subscriptionCustomerCompanyKeys: SubscriptionCustomerCompanyKey[],
) =>
	yup.object().shape({
		customerKey: yup
			.string()
			.max(100)
			.test("unique customer key", "customer key must be unique", function (value) {
				if (!this.parent.isNewCompany && this.parent.companyId && value) {
					return !subscriptionCustomerCompanyKeys.some(
						(x) => x.customerKey.toLowerCase() == value.toLowerCase().trim() && x.companyId == this.parent.companyId,
					);
				} else {
					return true;
				}
			})
			.label("customer key"),
		customerKeyDescription: yup.string().max(155).label("customer key description"),
		autoRenew: yup.bool().required(),
		isNewCompany: yup.bool(),
		companyId: yup
			.string()
			.when("isNewCompany", {
				is: false,
				then: yup.string().required(),
			})
			.label("company"),
		company: yup.object().when("isNewCompany", {
			is: true,
			then: yup.object().shape({
				companyName: yup
					.string()
					.label("company name")
					.required()
					.test("unique company name", "company name must be unique", function (value) {
						if (value) {
							return !existingCompanyNames.some((x) => x.toLowerCase() === value.toLowerCase());
						} else {
							return true;
						}
					})
					.max(250),
				identityProviderId: yup.string().label("identity provider").required(),
				uniqueCompanyIdentifier: yup
					.string()
					.label("unique company identifier")
					.required()
					.test("unique company unique identifier name", "unique company identifier must be unique", function (value) {
						if (value) {
							return !existingCompanyUniqueNames.some((x) => x.toLowerCase() === value.toLowerCase());
						} else {
							return true;
						}
					})
					.max(250),
			}),
		}),
		contacts: yup.array().of(
			yup.object().shape({
				email: yup.string().email().max(255).required().typeError("valid email is required").label("email"),
				firstName: yup.string().max(100).required().label("first name"),
				lastName: yup.string().max(100).required().label("last name"),
				phoneNumberCountryCode: yup
					.string()
					.matches(/^[0-9]+$/, {
						message: "phone number country code must be a number",
						excludeEmptyString: true,
					})
					.max(5)
					.when("phoneNumber", {
						is: (val) => !!val,
						then: yup.string().required(),
					})
					.label("phone number country code"),
				phoneNumber: yup
					.string()
					.matches(/^[0-9-.() ]+$/, {
						message: "phone number can consist of numbers, spaces, dashes, dots and parenthesis",
						excludeEmptyString: true,
					})
					.max(25)
					.label("phone number"),
			}),
		),
		isNewBillingPlan: yup.bool(),
		billingPlanId: yup
			.string()
			.when("isNewBillingPlan", {
				is: false,
				then: yup.string().required(),
			})
			.label("billing plan"),
		billingPlan: yup.object().when("isNewBillingPlan", {
			is: true,
			then: yup.object().shape({
				billingPlanName: yup
					.string()
					.required()
					.max(255)
					.test("unique billing plan name", "billing plan name must be unique", function (value) {
						if (value) {
							return !existingBillingPlanNames.some((x) => x.toLowerCase() === value.toLowerCase());
						} else {
							return true;
						}
					})
					.label("billing plan"),
				costFixed: yup.number().typeError("fixed cost must be a number").required().label("fixed cost"),
				costResource: yup.number().typeError("resource cost must be a number").required().label("resource cost"),
				costUsage: yup.number().typeError("usage cost must be a number").required().label("usage cost"),
				currency: yup.string().required().label("currency"),
				defaultAutoRenew: yup.bool().required(),
				invoiceAtStartOfPeriod: yup.bool().required(),
				isCustom: yup.bool().required(),
				isEnabledForPurchase: yup.bool().required(),
				licenseTypeId: yup.string().required().label("license type"),
				period: yup.string().required().label("period"),
				resourceCountMaximum: yup
					.number()
					.typeError("resource count maximum must be a number")
					.required()
					.integer()
					.label("resource cost maximum"),
				resourceCountMinimum: yup
					.number()
					.typeError("resource count minimum must be a number")
					.required()
					.integer()
					.label("resource cost minimum"),
				usageMetricMinimum: yup
					.number()
					.typeError("usage metric minimum must be a number")
					.required()
					.integer()
					.label("usage metric minimum"),
				usageMetricMaximum: yup
					.number()
					.typeError("usage metric maximum must be a number")
					.required()
					.integer()
					.label("usage metric maximum"),
				paymentTermInDays: yup
					.number()
					.typeError("payment term in days must be a number")
					.nullable()
					.integer()
					.label("payment term in days"),
				fixedFeeTypeLabelId: yup.string().label("fixed fee type label"),
				resourceFeeTypeLabelId: yup.string().label("resource fee type label"),
				usageFeeTypeLabelId: yup.string().label("usage fee type label"),
			}),
		}),
		productTierXBillingPlan: yup.object().shape({
			billingPlanId: yup.string(),
			productTierId: yup.string().required().label("product tier"),
			invoiceDescription: yup
				.string()
				.max(255)
				.required()
				.when("billingPlanId", { is: (billingPlanId) => !billingPlanId, then: yup.string().required() })
				.label("invoice description"),
		}),
		termsOfServiceAcceptance: yup.lazy(
			(value: { acceptor: string; dateAccepted: string; termsOfServiceId: string }) => {
				if (value.acceptor || value.dateAccepted || value.termsOfServiceId) {
					return yup.object().shape({
						acceptor: yup.string().max(250).required().label("acceptor"),
						dateAccepted: yup.date().required().typeError("date accepted must be a valid date").label("date accepted"),
						termsOfServiceId: yup.string().required().label("terms of service"),
					});
				}

				return yup.mixed().notRequired();
			},
		),
	});
